<template>
    <div>
        <internal-header v-if="!pdf_view" heading="Health & Welfare"/>
        <h4 class="d-print-block" v-show="pdf_view">{{ subtitle }}</h4>
        <health-welfare-report-filters
            v-if="$vuetify.breakpoint.mdAndUp && !pdf_view"
            v-model="filters"
            :constants="constants"
            :valuelists="valuelists"
            class="d-print-none"
            />
        <v-expansion-panels v-else-if="!pdf_view" flat>
            <v-expansion-panel>
                <v-expansion-panel-header hide-actions>
                    <template v-slot:default="{ open }">
                        <v-container class="py-0">
                            <v-row no-gutters>
                                <v-col cols="11" class="tw-text-lg">Filters<span v-show="!!filters_used">: {{ filters_used }}</span></v-col>
                                <v-col cols="1">
                                    <v-icon>
                                        <template v-if="open">mdi-minus-circle</template>
                                        <template v-else>mdi-plus-circle</template>
                                    </v-icon>
                                </v-col>
                            </v-row>
                        </v-container>
                    </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <health-welfare-report-filters
                        v-model="filters"
                        :constants="constants"
                        :valuelists="valuelists"
                    />
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <div class="tw-flex tw-flex-row tw-w-full tw-justify-between tw-items-center tw-pt-2 tw-pb-1 tw-border-b tw-border-t tw-border-dark-grey">
            <div class="flex-grow-1 tw-text-xl tw-font-light" v-show="num_records_filtered">{{ num_records_filtered }} Records</div>
            <div class="flex-grow-1 tw-text-xl tw-font-light" v-show="!num_records_filtered"> No records found</div>
            <div v-if="!pdf_view" @click="exportAsExcel()" class="flex-grow-0 tw-px-3 tw-text-black tw-underline tw-text-xs tw-font-normal tw-text-link tw-cursor-pointer d-print-none">Export List ( Excel )</div>
            <div v-if="!pdf_view" @click="exportAsPdf()" class="flex-grow-0 tw-px-3 tw-text-black tw-underline tw-text-xs tw-font-normal tw-text-link tw-cursor-pointer d-print-none">Print List ( PDF )</div>
        </div>
        <smart-table
            ref="report_list"
            endpoint="reports.list.healthwelfare"
            :appends="['hw_pst_calc', 'hw_std_calc', 'hw_net_calc', 'hw_total_calc']"
            :pdf_endpoint="route('reports.healthwelfare.queue', 'pdf').url()"
            :excel_endpoint="route('reports.healthwelfare.queue', 'excel').url()"
            :items="items"
            group-by="group_display_name"
            item-key="id"
            dense
            hide-default-header
            :hide-default-footer="pdf_view"
            :headers="headers"
            :search="filters.search"
            :multi-sort="true"
            :items-per-page="getDefaultItemsPerPage()"
            :footer-props="{
                'items-per-page-options': getDefaultItemsPerPageOptions()
            }"
            flat
            @fetch-complete="receivedRecordsUpdate"
            export_endpoint="reports.export.healthwelfare"
            export_endpoint_method="post"
            export_filename="export.xls"
            :auto_update="!!filters.production_id || !!filters.date_deposit"
        >

            <template v-slot:group="{group, options, isMobile, items, headers}">

                <tr>
                    <td :colspan="colspan_full">
                        <smart-table
                            :ref="`report_list_inner_${group}`"
                            :items="items"
                            :headers="headers_visible"
                            group-by="group_user_id"
                            item-key="id"
                            dense
                            hide-default-footer
                            :multi-sort="true"
                            :items-per-page="getDefaultItemsPerPage()"
                            :footer-props="{
                                'items-per-page-options': getDefaultItemsPerPageOptions()
                            }"
                            flat
                        >
                            <template v-slot:body.prepend="{items,originalItemsLength,pagination,options,groupedItems,headers,isMobile}">
                                <!--                PRODUCTION-->
                                <tr class="production-header">
                                    <th
                                        v-for="(header, header_index) in headers_visible"
                                        style="max-width: 185px !important; overflow: hidden;"
                                        :key="header.value"
                                        :class="{
                                            'text-end': header.summable,
                                            'tw-whitespace-nowrap': [0,1].includes(header_index),
                                        }">

                                        <template v-if="header_index === 0"><div style="min-width: 215px !important; max-width: 215px !important; overflow: hidden;"
                                        >{{ group || `${items[0]?.show_name}` }}</div></template>
                                        <template v-else-if="header_index === 1"></template>
                                        <template v-else>{{ getDataFromIntersection(summarizeItems(items, group), header) }}</template>
                                    </th>
                                </tr>

                            </template>

                            <template v-slot:group.header="{group, groupBy, isMobile, items, headers, isOpen, toggle, remove}" @click="toggle">
                                <th
                                    v-for="(header, header_index) in headers_visible"
                                    :key="header.value"
                                    :class="{
                                        'text-end': header.summable,
                                        'tw-whitespace-nowrap': [0,1].includes(header_index),
                                    }">

                                    <template v-if="header_index === 0"><span>{{ items[0].first_name }} {{ items[0].last_name }}</span></template>
                                    <template v-else-if="header_index === 1"></template>
                                    <template v-else>{{ getDataFromIntersection(summarizeItems(items, group), header) }}</template>
                                </th>

                            </template>

                            <template v-slot:item.hw_total_calc="{ isMobile, item, header, value, index}">
                                {{ value?.toFixed(2) }}
                            </template>

                            <template v-slot:item.hw_pst_calc="{ isMobile, item, header, value, index}">
                                {{ value?.toFixed(2) }}
                            </template>

                            <template v-slot:item.hw_std_calc="{ isMobile, item, header, value, index}">
                                {{ value?.toFixed(2) }}
                            </template>

                            <template v-slot:item.hw_net_calc="{ isMobile, item, header, value, index}">
                                {{ value?.toFixed(2) }}
                            </template>

                        </smart-table>
                    </td>
                </tr>
            </template>

            <template v-slot:item="{index, item, isExpanded, isMobile, isSelected, headers}">
                <tr></tr>
            </template>

<!--            Grand Total-->
            <template v-slot:body.append="{items, originalItemsLength,pagination,options,groupedItems,headers,isMobile}">
                <tr class="v-row-group__header"
                    v-show="pdf_view && Array.isArray(items) && items.length"
                >

                    <th
                        v-for="(header, header_index) in headers_visible"
                        :key="header.value"
                        :class="{
                                'text-end': header.summable || [0].includes(header_index),
                                'tw-whitespace-nowrap': [0,1].includes(header_index),
                            }">

                        <template v-if="header_index === 0"><span>GRAND TOTAL</span></template>
                        <template v-else-if="header_index === 1"></template>
                        <template v-else>{{ getDataFromIntersection(summarizeItems(items), header) }}</template>
                    </th>
                </tr>
            </template>

        </smart-table>

    </div>
</template>

<script>
    import SmartTable from "@/Generic/SmartTable";
    import AppLayout from "@/Layouts/AppLayout.vue";
    import InternalHeader from "@/Generic/InternalHeader.vue";
    import HealthWelfareReportFilters from "@/Pages/InternalPages/Reports/HealthWelfareReportFilters.vue";

    export default {
        name: "HealthWelfareReport",
        layout: (h, page) => {
            return h(AppLayout, [
                page
            ])
        },
        components: {
            InternalHeader,
            SmartTable,
            HealthWelfareReportFilters,
        },
        remember: {
            data: [
                'items'
            ]
        },
        props: {
            'constants': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'valuelists': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'items': {          // Will optionally accept a hardcoded list of items instead of getting smart table to fetch them
                type: Array,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'pdf_view': {          // Indicates if this instance is a PDF view
                type: Boolean,
                required: false,
                default: function () {
                    return false
                }
            },
            'date_deposit': {          // Indicates if this instance is a PDF view
                type: Array,
                required: false,
                default: function () {
                    return undefined
                }
            },
        },
        data() {
            return {
                constant: this.constants,
                filters: {
                    search: null,
                    date_deposit: this.date_deposit || null,
                    production_id: null,
                },
                fetch_data_response: null,
            }
        },
        computed: {
            headers() {
                return [
                    {value: 'id',               text: 'ID',             align: ' d-none',  class:'',                        width: '50px', sortable: false, filterable: false, summable: false,   },
                    {value: 'production_id',    text: 'PRODUCTION ID',  align: ' d-none',  class:'',                        width: '50px', sortable: false, filterable: true, summable: false, search: this?.filters?.production_id   },
                    {value: 'period_ending',    text: 'Week Ending',    align: 'start',    class:'tw-whitespace-nowrap',    width: '50px', sortable: false, filterable: false, summable: false,   },
                    {value: 'date_deposit',     text: 'Deposit Date',   align: 'start',    class:'tw-whitespace-nowrap',    width: '50px', sortable: false, filterable: true,  summable: false, search: this?.filters?.date_deposit  },
                    {value: 'HW_employer',      text: 'Employer',       align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                    {value: 'HW_daily',         text: 'Daily',          align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                    {value: 'hw_total_calc',    text: 'Total',          align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                    {value: 'hw_pst_calc',      text: 'PST',            align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                    {value: 'hw_std_calc',      text: 'STD',            align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                    {value: 'HW_employee',      text: 'Employee',       align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                    {value: 'hw_net_calc',      text: 'Net',            align: 'end',      class:'',                        width: '50px', sortable: false, filterable: false, summable: true,   },
                ]
            },
            filters_used() {
                let filters_used = Object.keys(this.filters).filter(filter => (!!this.filters[filter] && this.filters[filter]?.length))
                if (filters_used?.length === 4) return "All"
                if (!filters_used || filters_used?.length === 0) return ""
                return filters_used?.join(", ").toProperCase()
            },
            subtitle() {
                return this?.filters?.date_deposit?.join(' - ')
            },
            num_records_total() {
                return this.fetch_data_response?.data?.recordsTotal
            },
            num_records_filtered() {
                return this.fetch_data_response?.data?.recordsFiltered || this.items?.length
            },
            colspan_full() {
                return this.headers_visible?.length
            },
            headers_visible() {
                return this.headers?.filter(header => header.align !== ' d-none')
            },
            initial_date_range() {
                let now = new Date()
                let month_begin = new Date()
                month_begin.setDate(1)
                return [
                    month_begin.toISOString().split('T')[0],
                    now.toISOString().split('T')[0],
                ]
            },
        },
        mounted() {
            if (!this.filters.date_deposit) {
                this.filters.date_deposit = this.initial_date_range
            }
        },
        methods: {
            example() {
                console.log("This is just a test")
            },
            getSumOfAllColumns(array_to_sum) {
                return array_to_sum.reduce((accum, current) => {
                    Object.entries(current).forEach(([key, value]) => {
                        let include_this_item = this.headers.find(header => header.value === key)?.summable && (!!value || value === 0)
                        if (include_this_item) {
                            let value_numeric = Number(value)
                            let current_accumulation = typeof accum[key] !== 'undefined' ? Number(accum[key]) : 0
                            let sum = current_accumulation + value_numeric
                            let sum_display = Number.isInteger(sum) ? sum : (Math.round(sum * 100) / 100).toFixed(2)
                            accum[key] = sum_display
                        }
                    })
                    return {
                        ...accum
                    }
                }, {});
            },
            getDataFromIntersection(record, column) {
                let column_name = column?.hasOwnProperty('value') ? column.value : column
                return record[column_name]
            },
            receivedRecordsUpdate(response) {
                this.fetch_data_response = response
            },
            getDefaultItemsPerPage() {
                return this.pdf_view ? -1 : 20
            },
            getDefaultItemsPerPageOptions() {
                return this.pdf_view ? [-1] : [5, 10, 20, 30]
            },
            getItemsGroupedByPerson(items) {
                return Object.groupBy(items, (item) => item.group_user_id)
            },
            getFlatMapOfGroupedItems(items) {
                let by_person = this.getItemsGroupedByPerson(items)
                let flattened = Object.keys(by_person).flatMap((person_key, person_key_index) => {
                    let person_items = by_person[person_key] || []
                    let sum_person_items = this.getSumOfAllColumns(person_items)
                    return [{
                        ...sum_person_items,
                        id: `${person_key}_header_${person_key_index}`,
                        group_key_local: person_key,
                        group_user_id: person_items?.length ? person_items[0].group_user_id : null,
                        is_header: true,
                        text: person_items?.length ? `${person_items[0].first_name || ''} ${person_items[0].last_name || ''}` : null
                    }].concat(person_items)
                })
                return flattened
            },
            summarizeItems(items, name) {
                let sum_items = this.getSumOfAllColumns(items)
                return {
                    ...sum_items,
                    is_header: true,
                    text: name || null
                }
            },
            async exportAsExcel() {
                // await this.$refs.report_list.getExport() /* How it's done when you want to export without queue -- Only exports visible records */
                await this.$refs.report_list.getExcel()
            },
            async exportAsPdf() {
                await this.$refs.report_list.getPdf()
            },
        },
        watch: {
            items: {
                handler(newVal, oldVal) {
                    // this.items just got updated
                },
                deep: true,
                immediate: true,
            },
        },
    }
</script>

<style>
.row-pointer tbody tr {
    cursor: pointer;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td, .v-data-table > .v-data-table__wrapper > table > tbody > tr > th, .v-data-table > .v-data-table__wrapper > table > thead > tr > td, .v-data-table > .v-data-table__wrapper > table > thead > tr > th, .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td, .v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
    padding-left: 8px;
    padding-right: 8px;
}
.sub-header {
    background-color: #f3f6f4;
}

.production-header {
    background-color: #EEEEEE !important;
}

.theme--light.v-data-table .v-row-group__header{
    background-color: #f3f6f4 !important;
}

.theme--light.v-data-table .v-row-group__header th {
    padding-left: 16px !important;
}

@media print {
    .pagebreak {
        page-break-after: always;
        min-height: 1px;
    }

    html, body {
        font-size: 10pt; /* changing to 10pt has no impact */
    }
}
</style>
